import { createIndexedDevices, getDevices, hasChangedInput, isMediaDeviceInfo, mergeConstraints, reverseDeviceId, } from '@pexip/media-control';
import { createAsyncQueue, assert } from '@pexip/utils';
import { AUDIO_SETTINGS_KEYS, VIDEO_SETTINGS_KEYS, buildMedia, createMediaProcessor, hasSettingsChanged, isTrackEnded, isTrackMuted, } from './utils';
import { isMedia } from './typeGuard';
import { createModuleLogger } from './logger';
import { createGetUserMediaProcess, mergeMediaConstraints as mergeRequestConstraints, requestUserMediaWithRetry, } from './userMedia';
import { createMediaUpdater, updateStatusForTimeout } from './media';
import { GET_USER_MEDIA_TIMEOUT_MS } from './constants';
import { EMPTY_USER_MEDIA_STATUS } from './types';
const DEFAULT_QUEUE_DELAY_MS = 100;
const DEFAULT_QUEUE_DROP_LAST = false;
const DEFAULT_QUEUE_SIZE = 1;
const DEFAULT_QUEUE_THROTTLE_MS = 500;
const isPreviewInput = (value) => {
    if (isMediaDeviceInfo(value) || value === undefined) {
        return true;
    }
    return false;
};
const extractFeaturesToConstraints = (keysToLookFor, settings) => {
    if (!settings) {
        return {};
    }
    return keysToLookFor.reduce((set, key) => {
        if (settings[key] !== undefined) {
            // @ts-expect-error performance
            set[key] = settings[key];
        }
        return set;
    }, {});
};
const createEventHandler = (eventHandlers) => (key) => (callback) => {
    eventHandlers[key] = callback;
    return () => {
        eventHandlers[key] = undefined;
    };
};
const createAudioVideoProcessingSettingsChangeDetector = (getCurrentMedia, getPreviewMedia) => {
    const hasAudioSettingsChanged = hasSettingsChanged(AUDIO_SETTINGS_KEYS);
    const hasVideoSettingsChanged = hasSettingsChanged(VIDEO_SETTINGS_KEYS);
    return () => {
        const currentMedia = getCurrentMedia?.();
        const previewMedia = getPreviewMedia();
        const { audio: mainAudio, video: mainVideo } = currentMedia?.getSettings() ?? { audio: {}, video: {} };
        const { audio: previewAudio, video: previewVideo } = previewMedia?.getSettings() ?? { audio: {}, video: {} };
        const changed = hasAudioSettingsChanged(mainAudio, previewAudio) ||
            hasVideoSettingsChanged(mainVideo, previewVideo);
        return changed;
    };
};
export const createPreviewStreamController = ({ getCurrentDevices, getCurrentMedia, updateMainStream, onEnded, mainMediaSignal, queueOptions = {
    size: DEFAULT_QUEUE_SIZE,
    throttleInMS: DEFAULT_QUEUE_THROTTLE_MS,
    delayInMS: DEFAULT_QUEUE_DELAY_MS,
    dropLast: DEFAULT_QUEUE_DROP_LAST,
}, audioProcessors, videoProcessors, getDefaultConstraints = () => ({}), signals, stopVideoTrackAsMute = () => false, getUserMediaTimeoutInMS = GET_USER_MEDIA_TIMEOUT_MS, }) => {
    const queue = createAsyncQueue(queueOptions);
    const eventHandlers = {};
    const internalProps = {
        media: undefined,
        updatingPreviewAudio: false,
        updatingPreviewVideo: false,
        updatingMain: false,
        initialized: false,
        signals,
        keepVideoMuted: false,
    };
    const logger = createModuleLogger({
        module: 'PreviewStreamController',
        eventHandlers,
        props: internalProps,
    });
    const cleanUpMainSubscription = () => {
        if (eventHandlers.unsubscribeMain) {
            eventHandlers.unsubscribeMain();
            eventHandlers.unsubscribeMain = undefined;
        }
    };
    const props = new Proxy(internalProps, {
        get: (target, p) => {
            return target[p];
        },
        set: (target, p, value) => {
            if (target[p] === value) {
                return true;
            }
            logger.debug({ oldValue: target[p], newValue: value }, `Update Props[${p}]`);
            switch (p) {
                case 'media': {
                    if (!isMedia(value)) {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    // Unsubscribe main stream signal on change of inputs
                    cleanUpMainSubscription();
                    return true;
                }
                case 'audioInput':
                case 'videoInput': {
                    if (!isPreviewInput(value)) {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    return true;
                }
                case 'updatingMain':
                case 'updatingPreviewVideo':
                case 'updatingPreviewAudio': {
                    if (typeof value !== 'boolean') {
                        return false;
                    }
                    target[p] = value;
                    eventHandlers[p]?.(value);
                    return true;
                }
                default:
                    Reflect.set(target, p, value);
                    return true;
            }
        },
    });
    const subscriptions = [
        props.signals.onMediaTrackMuted?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioMuted?.(track.muted);
            }
            else {
                eventHandlers.videoMuted?.(props.keepVideoMuted || track.muted);
            }
        }),
        props.signals.onMediaTrackSuspended?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioSuspended?.(track.suspended);
            }
            else {
                eventHandlers.videoSuspended?.(track.suspended);
            }
        }),
        props.signals.onMediaTrackResumed?.add(track => {
            if (track.kind === 'audioinput') {
                eventHandlers.audioSuspended?.(track.suspended);
            }
            else {
                eventHandlers.videoSuspended?.(track.suspended);
            }
        }),
        props.signals.onAddTrack?.add(track => {
            switch (track.kind) {
                case 'audio': {
                    eventHandlers.audioMuted?.(isTrackMuted(track) || isTrackEnded(track));
                    break;
                }
                case 'video': {
                    eventHandlers.videoMuted?.(props.keepVideoMuted ||
                        isTrackMuted(track) ||
                        isTrackEnded(track));
                    break;
                }
            }
        }),
        props.signals.onRemoveTrack?.add(track => {
            switch (track.kind) {
                case 'audio': {
                    eventHandlers.audioMuted?.(isTrackMuted(track) || isTrackEnded(track));
                    break;
                }
                case 'video': {
                    eventHandlers.videoMuted?.(props.keepVideoMuted ||
                        isTrackMuted(track) ||
                        isTrackEnded(track));
                    break;
                }
            }
        }),
    ];
    /**
     * Read devices revealed by preview permission grants, independently of the main cache.
     * @example const devices = await getPreviewDevices();
     */
    const getPreviewDevices = async () => createIndexedDevices(await getDevices());
    const getUserMedia = createGetUserMediaProcess({
        getUserMedia: requestUserMediaWithRetry(getPreviewDevices),
        signals: props.signals,
        getCurrentDevices: getPreviewDevices,
        stopVideoTrackAsMute,
        updateMedia: constraints => updateMedia(constraints),
    });
    const mergeMediaConstraints = (constraints) => {
        const { audio, video } = getDefaultConstraints();
        return {
            audio: audio === false
                ? false
                : mergeConstraints(audio)(constraints.audio),
            video: video === false
                ? false
                : mergeConstraints(video)(constraints.video),
        };
    };
    const processMedia = createMediaProcessor({
        audioProcessors,
        videoProcessors,
        onProcessingError(error, track) {
            logger.error({ error, track }, 'Failed to process media track');
        },
    });
    const invalidatedTracks = new WeakSet();
    const processAndUpdateMedia = async (media, tracks, sync = false) => {
        try {
            const processedTracks = await processMedia(tracks);
            for (const [idx, track] of processedTracks.entries()) {
                const originTrack = tracks.at(idx);
                assert(originTrack, 'Processed track should always has the original track in the same order');
                if (originTrack.stopped ||
                    media !== props.media ||
                    invalidatedTracks.has(originTrack) ||
                    !media.isCurrentTrack(originTrack)) {
                    if (originTrack.id !== track.id) {
                        await track.release(() => false);
                    }
                    continue;
                }
                // Only replace track when they are not the same
                if (originTrack.id !== track.id) {
                    if (sync) {
                        if (originTrack.track && track.track) {
                            track.track.enabled = originTrack.track.enabled;
                        }
                    }
                    media.removeTrack(originTrack);
                    media.addTrack(track);
                }
            }
        }
        catch (error) {
            logger.error({ error, tracks }, 'Failed to process media tracks');
        }
    };
    const updateMediaProcess = createMediaUpdater({
        getUserMedia,
        signals: props.signals,
        getDefaultConstraints,
        getCurrentDevices: () => Promise.resolve(getCurrentDevices()),
        isTrackInvalidated: track => invalidatedTracks.has(track),
        onMediaTracksChanged: (media, tracks) => {
            if (props.keepVideoMuted) {
                for (const track of tracks) {
                    if (track.kind === 'videoinput') {
                        track.mute(true);
                    }
                }
                props.keepVideoMuted = false;
            }
            props.media = media;
            props.initialized = true;
            queue.enqueue(async () => {
                await processAndUpdateMedia(media, tracks, false);
            });
        },
    });
    const mediaUpdateGeneration = { audio: 0, video: 0 };
    const previewInputUpdateGeneration = { audio: 0, video: 0 };
    const activeMediaUpdates = new Set();
    const updateMedia = async (constraints, force = false) => {
        const updating = {
            audio: constraints.audio !== undefined,
            video: constraints.video !== undefined,
        };
        const generation = {
            audio: updating.audio
                ? ++mediaUpdateGeneration.audio
                : mediaUpdateGeneration.audio,
            video: updating.video
                ? ++mediaUpdateGeneration.video
                : mediaUpdateGeneration.video,
        };
        const abortController = new AbortController();
        activeMediaUpdates.add(abortController);
        const isCurrent = () => (!updating.audio ||
            generation.audio === mediaUpdateGeneration.audio) &&
            (!updating.video ||
                generation.video === mediaUpdateGeneration.video);
        const mergedConstraints = mergeMediaConstraints(constraints);
        if (!props.media) {
            props.media = buildMedia({
                constraints: mergedConstraints,
                permission: { audio: 'prompt', video: 'prompt' },
                devices: getCurrentDevices(),
                status: EMPTY_USER_MEDIA_STATUS,
                stream: undefined,
                signals,
                tracks: [],
            });
        }
        const media = props.media;
        media.setOriginalConstraints(mergeRequestConstraints(media.getOriginalConstraints(), mergedConstraints));
        let timeoutId = 0;
        const timeout = new Promise(resolve => {
            timeoutId = window.setTimeout(() => {
                if (isCurrent()) {
                    if (updating.audio) {
                        mediaUpdateGeneration.audio += 1;
                    }
                    if (updating.video) {
                        mediaUpdateGeneration.video += 1;
                    }
                    abortController.abort();
                    for (const track of media.getTracks()) {
                        if ((updating.audio && track.kind === 'audioinput') ||
                            (updating.video && track.kind === 'videoinput')) {
                            invalidatedTracks.add(track);
                        }
                    }
                    media.status = updateStatusForTimeout(media.status, updating, getCurrentDevices(), media.getTracks());
                }
                resolve();
            }, getUserMediaTimeoutInMS);
        });
        try {
            await Promise.race([
                updateMediaProcess(mergedConstraints, media, isCurrent, abortController.signal, force),
                timeout,
            ]);
        }
        finally {
            window.clearTimeout(timeoutId);
            activeMediaUpdates.delete(abortController);
        }
    };
    const initFromMain = (mainMedia) => {
        if (!mainMedia?.stream) {
            return;
        }
        try {
            const clonedMedia = mainMedia.clone(props.signals, 'Preview');
            props.media = clonedMedia;
            queue.enqueue(async () => {
                await processAndUpdateMedia(clonedMedia, clonedMedia.getTracks(), true);
            });
            props.audioInput = mainMedia.audioInput;
            props.videoInput = mainMedia.videoInput;
            props.originalMainAudioInput = mainMedia.audioInput;
        }
        catch (error) {
            logger.error({ error }, 'Failed to post process media');
        }
        finally {
            props.initialized = true;
        }
    };
    const mainMedia = getCurrentMedia();
    if (mainMedia?.stream) {
        initFromMain(mainMedia);
    }
    else {
        eventHandlers.unsubscribeMain = mainMediaSignal.add(initFromMain);
    }
    const replaceMainStream = async (constraints) => {
        logger.debug({ constraints }, 'Replacing main stream');
        props.updatingMain = true;
        await updateMainStream(constraints);
        props.updatingMain = false;
    };
    const updatePreviewMedia = async (constraints) => {
        logger.debug({ constraints }, 'Requesting a new preview stream');
        await updateMedia(constraints);
        logger.debug({ media: props.media }, 'Preview media updated');
    };
    const releaseAudio = async () => {
        for (const track of props.media?.getAudioTracks() ?? []) {
            await track.release();
        }
    };
    const releaseVideo = async () => {
        for (const track of props.media?.getVideoTracks() ?? []) {
            await track.release();
        }
    };
    const updateAudioInput = async (input) => {
        const generation = ++previewInputUpdateGeneration.audio;
        const isCurrentUpdate = () => generation === previewInputUpdateGeneration.audio;
        const request = { audio: { device: { exact: input } } };
        try {
            props.audioInput = input;
            props.updatingPreviewAudio = true;
            if (props.audioInput === undefined) {
                return await releaseAudio();
            }
            await updatePreviewMedia(request);
        }
        catch (error) {
            if (!isCurrentUpdate()) {
                return;
            }
            if (error instanceof Error) {
                const errorName = error.name === 'Error' ? error.message : error.name;
                if (errorName === 'NotReadableError') {
                    try {
                        logger.debug({ input }, 'NotReadableError, trying to sync main stream AudioInput');
                        await replaceMainStream({
                            audio: input,
                            video: getCurrentMedia()?.videoInput,
                        });
                        if (!isCurrentUpdate()) {
                            return;
                        }
                        await updatePreviewMedia(request);
                    }
                    catch (err) {
                        if (!isCurrentUpdate()) {
                            return;
                        }
                        logger.warn({ error: err, input }, 'Unable to recover NotReadableError AudioInput for preview');
                        if (err instanceof Error) {
                            return eventHandlers.audioInputError?.(err);
                        }
                        throw err;
                    }
                }
                else {
                    logger.warn({ error, input }, 'Unable to update AudioInput for preview');
                    eventHandlers.audioInputError?.(error);
                }
            }
            else {
                logger.error({ error, input }, 'Unable to update AudioInput for preview');
                throw error;
            }
        }
        finally {
            if (isCurrentUpdate()) {
                props.updatingPreviewAudio = false;
            }
        }
    };
    const updateVideoInput = async (input, syncMuteState = true) => {
        const generation = ++previewInputUpdateGeneration.video;
        const isCurrentUpdate = () => generation === previewInputUpdateGeneration.video;
        try {
            props.videoInput = input;
            props.updatingPreviewVideo = true;
            if (input === undefined) {
                return await releaseVideo();
            }
            props.keepVideoMuted = syncMuteState && !!props.media?.videoMuted;
            await updatePreviewMedia({ video: { device: { exact: input } } });
            return;
        }
        catch (error) {
            if (!isCurrentUpdate()) {
                return;
            }
            if (error instanceof Error) {
                logger.warn({ error, input }, 'Unable to update VideoInput for preview');
                return eventHandlers.videoInputError?.(error);
            }
            throw error;
        }
        finally {
            if (isCurrentUpdate()) {
                props.updatingPreviewVideo = false;
            }
        }
    };
    /**
     * Reacquire one lane without depending on device enumeration.
     * @example await retryPreviewInput('video');
     */
    const retryPreviewInput = async (kind) => {
        const generation = ++previewInputUpdateGeneration[kind];
        const isCurrentUpdate = () => generation === previewInputUpdateGeneration[kind];
        const updatingKey = kind === 'audio' ? 'updatingPreviewAudio' : 'updatingPreviewVideo';
        const inputKey = kind === 'audio' ? 'audioInput' : 'videoInput';
        const errorKey = kind === 'audio' ? 'audioInputError' : 'videoInputError';
        props[updatingKey] = true;
        if (kind === 'video') {
            props.keepVideoMuted = false;
        }
        try {
            await updateMedia({ [kind]: props.media?.getOriginalConstraints()[kind] || true }, true);
            const input = props.media?.[inputKey];
            if (input) {
                props[inputKey] = input;
            }
        }
        catch (error) {
            if (!isCurrentUpdate()) {
                return;
            }
            if (!(error instanceof Error)) {
                throw error;
            }
            logger.warn({ error, kind }, 'Unable to retry preview input');
            eventHandlers[errorKey]?.(error);
        }
        finally {
            if (isCurrentUpdate()) {
                props[updatingKey] = false;
            }
        }
    };
    const cleanup = async () => {
        logger.debug('Cleanup preview controller');
        mediaUpdateGeneration.audio += 1;
        mediaUpdateGeneration.video += 1;
        for (const abortController of activeMediaUpdates) {
            abortController.abort();
        }
        activeMediaUpdates.clear();
        // Drop pending jobs, then enqueue release so it runs after any
        // in-flight `processAndUpdateMedia`. Releasing concurrently with an
        // in-flight `videoProcessor.process()` would tear down the worker
        // pipeline mid-flight.
        queue.empty();
        const media = props.media;
        if (media) {
            await new Promise((resolve, reject) => {
                queue.enqueue(async () => {
                    try {
                        await media.release();
                        resolve();
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            });
        }
        cleanUpMainSubscription();
        for (const unsub of subscriptions) {
            unsub?.();
        }
        subscriptions.length = 0;
        props.audioInput = undefined;
        props.videoInput = undefined;
        onEnded?.();
    };
    const hasChangedAudioInput = () => hasChangedInput(props.originalMainAudioInput, props.audioInput);
    const hasChangedVideoInput = () => hasChangedInput(getCurrentMedia()?.videoInput, props.videoInput);
    const hasAudioVideoProcessingSettingsChanged = createAudioVideoProcessingSettingsChangeDetector(getCurrentMedia, () => props.media);
    const hasChanges = () => hasChangedAudioInput() ||
        hasChangedVideoInput() ||
        hasAudioVideoProcessingSettingsChanged();
    const applyChanges = async (force = false) => {
        const { audioInput, videoInput } = props;
        const { audio: previewAudio, video: previewVideo } = props.media?.getSettings() ?? { audio: {}, video: {} };
        if (force || hasChanges()) {
            const audioSettings = extractFeaturesToConstraints(AUDIO_SETTINGS_KEYS, previewAudio);
            // Omit the width & height settings from the preview so that the one
            // from the main can be applied
            const { width: _width, height: _height, edgeBlurAmount: _edgeBlurAmount, lightWrapBlurAmount: _lightWrapBlurAmount, backgroundBlurAmount: _backgroundBlurAmount, ...videoSettings } = extractFeaturesToConstraints(VIDEO_SETTINGS_KEYS, previewVideo);
            logger.info({ audioInput, videoInput, audioSettings, videoSettings }, 'Apply changes to main');
            try {
                await replaceMainStream({
                    audio: audioInput
                        ? { device: { exact: audioInput }, ...audioSettings }
                        : false,
                    video: videoInput
                        ? { device: { exact: videoInput }, ...videoSettings }
                        : false,
                });
                props.originalMainAudioInput = props.audioInput;
            }
            catch (error) {
                if (error instanceof Error) {
                    logger.warn({ error }, 'Unable to apply changes to main');
                    return eventHandlers.applyChangesError?.(error);
                }
                throw error;
            }
        }
    };
    const revertChanges = async () => {
        const mainMedia = getCurrentMedia();
        const mainStream = mainMedia?.stream;
        if (mainMedia && mainStream) {
            // Workaround for iOS Safari bug
            // https://bugs.webkit.org/show_bug.cgi?id=179363
            // https://github.com/pexip/mcu/issues/43138
            const mainMuted = !!mainStream.getTracks().some(t => t.muted);
            const current = mainMedia.audioInput;
            const prev = props.originalMainAudioInput;
            const audioChanged = hasChangedInput(prev, current);
            if (mainMuted || audioChanged) {
                logger.info({
                    mainMuted,
                    audioChanged,
                    currentAudioInput: current,
                    prevAudioInput: prev,
                    mainMedia,
                }, 'Reverting Changes to main');
                try {
                    await replaceMainStream({
                        audio: prev,
                        video: getCurrentMedia()?.videoInput,
                    });
                }
                catch (error) {
                    if (error instanceof Error) {
                        logger.warn({ error }, 'Unable to revert changes to main');
                        return eventHandlers.revertChangesError?.(error);
                    }
                    throw error;
                }
            }
        }
    };
    const updateInput = (hasChanged, updater) => (id) => {
        const [input] = id
            ? getCurrentDevices().get(reverseDeviceId(id))
            : [];
        if (hasChanged(input)) {
            queue.enqueue(async () => {
                await updater(input);
            });
        }
    };
    const toEvenHandler = createEventHandler(eventHandlers);
    return {
        get media() {
            return props.media;
        },
        get audioInputChanged() {
            return hasChangedAudioInput();
        },
        get videoInputChanged() {
            return hasChangedVideoInput();
        },
        get inputChanged() {
            return hasChanges();
        },
        get audioInput() {
            return props.audioInput;
        },
        get videoInput() {
            return props.videoInput;
        },
        get updatingPreviewAudio() {
            return props.updatingPreviewAudio;
        },
        get updatingPreviewVideo() {
            return props.updatingPreviewVideo;
        },
        get updatingMain() {
            return props.updatingMain;
        },
        updatePreviewInput: input => {
            switch (input?.kind) {
                case 'audioinput': {
                    return updateAudioInput(input);
                }
                case 'videoinput': {
                    return updateVideoInput(input, false);
                }
            }
        },
        retryPreviewInput,
        updateAudioInput: updateInput(input => props.initialized && hasChangedInput(props.audioInput, input), updateAudioInput),
        updateVideoInput: updateInput(input => props.initialized && hasChangedInput(props.videoInput, input), updateVideoInput),
        onMediaChanged: toEvenHandler('media'),
        onAudioInputChanged: toEvenHandler('audioInput'),
        onVideoInputChanged: toEvenHandler('videoInput'),
        onAudioInputError: toEvenHandler('audioInputError'),
        onVideoInputError: toEvenHandler('videoInputError'),
        onApplyChangesError: toEvenHandler('applyChangesError'),
        onRevertChangesError: toEvenHandler('revertChangesError'),
        onUpdatingPreviewAudio: toEvenHandler('updatingPreviewAudio'),
        onUpdatingPreviewVideo: toEvenHandler('updatingPreviewVideo'),
        onUpdatingMain: toEvenHandler('updatingMain'),
        onAudioMuted: toEvenHandler('audioMuted'),
        onVideoMuted: toEvenHandler('videoMuted'),
        onAudioSuspended: toEvenHandler('audioSuspended'),
        onVideoSuspended: toEvenHandler('videoSuspended'),
        applyChanges,
        revertChanges,
        cleanup,
    };
};
//# sourceMappingURL=previewController.js.map